import React, { useEffect, useRef, useState } from "react";
import Utils from "../../Utils";
import { useParams } from "react-router-dom";
import axios from "axios";
import validator from "validator";

const ResetPassword = () => {
  const frmRef = useRef(null);
  const { token } = useParams();

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = loading, false = invalid, true = valid
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    document.body.className =
      "bg-gray-100 flex items-center justify-center min-h-screen px-4 py-6";
    // Validate token on mount
    axios
      .post(
        Utils.apiUrl("frontend/auth/validate-reset-token"),
        { token },
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(res.data.message || "Invalid or expired token");
        }
      })
      .catch((err) => {
        setTokenValid(false);
        setError(err.message || "Invalid or expired token");
      });
    return () => {
      document.body.className = "bg-gray-50";
    };
  }, [token]);

  const validatePassword = () => {
    const frm = frmRef.current;
    if (
      !validator.isStrongPassword(frm.newpwd.value, {
        minLength: 8,
        maxLength: 12,
        minLowercase: 1,
        minUppercase: 1,
        minNumbers: 1,
        minSymbols: 1,
      })
    ) {
      setError(
        "Password must be strong (8-12 chars, uppercase, number & symbol)",
      );
      return false;
    }

    if (frm.newpwd.value !== frm.confpwd.value) {
      setError("Passwords do not match");
      return false;
    }
    setError(false);
    return true;
  };

  const submitForm = (e) => {
    e.preventDefault();
    const frm = frmRef.current;

    if (!validatePassword()) return;

    setProcessing(true);
    setSuccess(false);

    const frmdata = new FormData(frm);

    axios
      .post(Utils.apiUrl("frontend/auth/resetpwd"), frmdata, Utils.apiHeaders())
      .then((res) => {
        console.log(res);
        if (res.data.success) {
          setSuccess(true);
          //Utils.setUserData(false);
          frm.reset();
          window.setTimeout(() => {
            window.location.href = process.env.PUBLIC_URL + res.data.navigateTo;
          }, 2000);
        } else {
          throw res.data;
        }
      })
      .catch((err) => {
        console.log(err);
        setError(err.message || "Failed to reset password");
      })
      .finally(() => setProcessing(false));
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-xl loginWrap">
      <div className="flex justify-center pb-8">
        <img
          src={`${process.env.PUBLIC_URL}/images/logo.png`}
          alt="Marriott Bonvoy"
          className="logo max-w-full"
        />
      </div>
      <h2 className="text-2xl font-bold text-center mb-6">
        Reset your password
      </h2>
      <div className="bg-white rounded-lg shadow p-6">
        {tokenValid === null && (
          <div className="bg-blue-100 text-blue-700 p-3 rounded mb-4">
            Validating token...
          </div>
        )}
        {tokenValid === false && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            {error}
          </div>
        )}
        {tokenValid === true && (
          <>
            {/* Processing */}
            {processing && (
              <div className="bg-blue-100 text-blue-700 p-3 rounded mb-4">
                Processing password change...
              </div>
            )}
            {/* Success */}
            {success && (
              <div className="bg-green-100 text-green-700 p-3 rounded mb-4">
                Password updated successfully.
              </div>
            )}
            {/* Error */}
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
                {error}
              </div>
            )}
            {!processing && (
              <form ref={frmRef} onSubmit={submitForm} className="space-y-4">
                <input type="hidden" name="token" value={token} />
                <div>
                  <label className="block text-sm font-medium mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newpwd"
                      onChange={validatePassword}
                      required
                      className="w-full px-3 py-2 pr-10 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((value) => !value)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                      aria-label={
                        showNewPassword
                          ? "Hide new password"
                          : "Show new password"
                      }
                    >
                      <i
                        className={`fas ${showNewPassword ? "fa-eye-slash" : "fa-eye"}`}
                      ></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confpwd"
                      onChange={validatePassword}
                      required
                      className="w-full px-3 py-2 pr-10 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((value) => !value)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                    >
                      <i
                        className={`fas ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                      ></i>
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                  <ul>
                    <li>Minimum 8 - 12 characters</li>
                    <li>At least 1 uppercase letter</li>
                    <li>At least 1 lowercase letter</li>
                    <li>At least 1 digit</li>
                    <li>At least 1 special character (e.g., !@#$%^&*)</li>
                  </ul>
                </div>
                <div className="text-right">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-medium"
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
